// src/membership/ProfilePage.js

import React, { useState, useEffect, useRef } from 'react';
// DEĞİŞİKLİK: Doğrudan Firestore ve Auth güncellemesi yerine Cloud Functions kullanacağız.
// Bu yüzden 'doc' ve 'setDoc' importlarını kaldırıp, 'getFunctions' ve 'httpsCallable' ekliyoruz.
// 'updateProfile' importunu da kaldırıyoruz.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuth } from '../context/AuthContext.js';
// DEĞİŞİKLİK: Firebase config'den 'auth' importuna ihtiyacımız devam ediyor, 
// çünkü Cloud Function'ı başlatmak için app nesnesine ondan erişeceğiz. 'db' importu artık gereksiz.
import { auth, storage } from '../config/firebaseConfig.js';

const defaultAvatar = `${process.env.PUBLIC_URL}/images/thumb.jpg`;

const ProfilePage = () => {
    const { currentUser, userData } = useAuth();
    // State yapında herhangi bir değişiklik yapmamıza gerek yok, mevcut haliyle harika.
    const [profileData, setProfileData] = useState({
        displayName: '',
        profession: '',
        phone: '',
        address: '',
        twitter: '',
        linkedin: '',
        website: ''
    });
    const [photoURL, setPhotoURL] = useState(defaultAvatar);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        // Bu kısım da gayet iyi çalışıyor, olduğu gibi bırakıyoruz.
        // userData geldiğinde formu dolduruyor.
        if (currentUser && userData) {
            setProfileData({
                displayName: userData.displayName || '',
                profession: userData.profession || '',
                phone: userData.phone || '',
                address: userData.address || '',
                twitter: userData.twitter || '',
                linkedin: userData.linkedin || '',
                website: userData.website || ''
            });
            // DEĞİŞİKLİK: Firestore belgesindeki alan adı 'photoURL' olmalı.
            // Kodunda zaten bu şekilde, sadece tutarlılık için belirtiyorum.
            setPhotoURL(userData.photoURL || defaultAvatar);
        }
    }, [currentUser, userData]);

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    // DEĞİŞİKLİK: Bu fonksiyonun iç mantığını Cloud Function'ı çağıracak şekilde güncelliyoruz.
    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        if (!currentUser) {
            setError("No user is logged in. Please refresh and try again.");
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        let finalPhotoURL = photoURL;

        try {
            // Resim yükleme mantığın mükemmel, bunu aynen koruyoruz.
            if (imageFile) {
                const storageRef = ref(storage, `profilePictures/${currentUser.uid}`);
                const uploadTask = uploadBytesResumable(storageRef, imageFile);

                // Promise yapısı ile yüklemenin bitmesini beklemek çok doğru bir yaklaşım.
                await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(progress);
                        },
                        (error) => {
                            console.error("Upload error:", error);
                            reject(error);
                        },
                        async () => {
                            finalPhotoURL = await getDownloadURL(uploadTask.snapshot.ref);
                            setPhotoURL(finalPhotoURL);
                            resolve();
                        }
                    );
                });
            }

            // --- YENİ VE GÜVENLİ GÜNCELLEME YÖNTEMİ ---
            // 1. Frontend'den 'updateProfile' ve 'setDoc' çağırmak yerine,
            //    tüm verileri bir obje içinde topluyoruz.
            const dataToUpdate = {
                ...profileData,
                photoURL: finalPhotoURL,
            };

            // 2. 'updateUserProfile' isimli Cloud Function'ımızı çağırıyoruz.
            //    Bu fonksiyon hem Auth hem de Firestore tarafındaki güncellemeyi
            //    sunucu tarafında güvenli bir şekilde yapacak.
            const functions = getFunctions(auth.app); // Firebase app nesnesini auth'tan alıyoruz.
            const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
            const result = await updateUserProfile(dataToUpdate);

            // 3. Cloud Function'dan dönen başarılı mesajını state'e set ediyoruz.
            setSuccess(result.data.message || 'Profile updated successfully!');
            
            setImageFile(null);
            setImagePreview(null);
            setUploadProgress(0);

        } catch (err) {
            console.error("Profile update error:", err);
            // Hata mesajını Cloud Function'dan veya genel bir mesaj olarak alabiliriz.
            setError(err.message || 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!currentUser) {
        return <p className="text-center mt-5">Please log in to view your profile.</p>;
    }

    // JSX yapın ve tasarımın çok güzel, hiçbir değişiklik yapmıyoruz.
    return (
        <div className="bg-light py-5" style={{ minHeight: 'calc(100vh - 80px)' }}>
            <div className="container">
                {/* ... JSX kodunun geri kalanı tamamen aynı ... */}
                <div className="row align-items-start">
                    <div className="col-lg-4">
                        <div className="card shadow-sm mb-4">
                            <div className="card-body text-center">
                                <div className="position-relative d-inline-block">
                                    <img 
                                        src={imagePreview || photoURL} 
                                        alt="Profile" 
                                        className="rounded-circle" 
                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }} 
                                    />
                                    <button 
                                        className="btn btn-light btn-sm rounded-circle position-absolute"
                                        style={{ bottom: '5px', right: '5px' }}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.776 48.776 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                                        </svg>
                                    </button>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        className="d-none" 
                                        accept="image/png, image/jpeg"
                                    />
                                </div>
                                {uploadProgress > 0 && uploadProgress < 100 && (
                                    <div className="progress mt-3" style={{height: '5px'}}>
                                        <div className="progress-bar" role="progressbar" style={{width: `${uploadProgress}%`}}></div>
                                    </div>
                                )}
                                <h4 className="my-3">{profileData.displayName || 'Set Your Name'}</h4>
                                <p className="text-muted mb-1">{profileData.profession || 'Set Your Profession'}</p>
                                <p className="text-muted mb-4">{profileData.address || 'Set Your Address'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        <div className="card shadow-sm mb-4">
                            <div className="card-body">
                                <h4 className="mb-4">Profile Details</h4>
                                {error && <div className="alert alert-danger">{error}</div>}
                                {success && <div className="alert alert-success">{success}</div>}

                                <form onSubmit={handleProfileUpdate}>
                                    <div className="row mb-3">
                                        <div className="col-sm-3"><h6 className="mb-0">Full Name</h6></div>
                                        <div className="col-sm-9"><input type="text" className="form-control" name="displayName" value={profileData.displayName} onChange={handleChange} /></div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-3"><h6 className="mb-0">Email</h6></div>
                                        <div className="col-sm-9"><input type="email" className="form-control" value={currentUser.email} disabled /></div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-3"><h6 className="mb-0">Profession</h6></div>
                                        <div className="col-sm-9"><input type="text" className="form-control" name="profession" value={profileData.profession} onChange={handleChange} /></div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-3"><h6 className="mb-0">Phone</h6></div>
                                        <div className="col-sm-9"><input type="text" className="form-control" name="phone" value={profileData.phone} onChange={handleChange} /></div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-3"><h6 className="mb-0">Address</h6></div>
                                        <div className="col-sm-9"><input type="text" className="form-control" name="address" value={profileData.address} onChange={handleChange} /></div>
                                    </div>

                                    <hr />
                                    <h5 className="my-4">Social Links</h5>

                                    <div className="input-group mb-3">
                                        <span className="input-group-text">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{width: '20px', height: '20px'}}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A11.953 11.953 0 0112 16.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0021 12a8.959 8.959 0 00-2.284-5.253" />
                                            </svg>
                                        </span>
                                        <input type="text" className="form-control" placeholder="Website" name="website" value={profileData.website} onChange={handleChange} />
                                    </div>
                                    <div className="input-group mb-3">
                                        <span className="input-group-text">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-twitter-x" viewBox="0 0 16 16"><path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z"/></svg>
                                        </span>
                                        <input type="text" className="form-control" placeholder="Twitter URL" name="twitter" value={profileData.twitter} onChange={handleChange} />
                                    </div>
                                    <div className="input-group mb-3">
                                        <span className="input-group-text">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-linkedin" viewBox="0 0 16 16"><path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"/></svg>
                                        </span>
                                        <input type="text" className="form-control" placeholder="LinkedIn URL" name="linkedin" value={profileData.linkedin} onChange={handleChange} />
                                    </div>
                                    
                                    <div className="row mt-4">
                                        <div className="col-sm-12">
                                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                                {loading ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;